import { For, Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@solidjs/router";
import styles from "./CommandPalette.module.css";
import { isFeatureEnabled } from "../services/featureFlags";
import { useTranslation } from "../hooks/useTranslation";

interface PaletteAction {
  id: string;
  labelKey: string;
  hint?: string;
  icon?: string;
  href?: string;
  run?: () => void;
  flag?: string;
}

const ROUTE_ACTIONS: PaletteAction[] = [
  { id: "nav.dashboard", labelKey: "DASHBOARD", icon: "fa-gauge", href: "/dashboard" },
  { id: "nav.bots", labelKey: "BOT_REGISTRY_NAV", icon: "fa-id-card", href: "/bots" },
  { id: "nav.botengine", labelKey: "BOTENGINE", icon: "fa-server", href: "/botengine" },
  { id: "nav.multi", labelKey: "MULTI_NAV", icon: "fa-tower-broadcast", href: "/multi", flag: "MULTI_ORCHESTRATE" },
  { id: "nav.mybots", labelKey: "MYBOTS", icon: "fa-robot", href: "/mybots" },
  { id: "nav.mycommands", labelKey: "MYCOMMANDS", icon: "fa-person-running", href: "/mycommands" },
  { id: "nav.mygrants", labelKey: "MYGRANTS", icon: "fa-share-nodes", href: "/mygrants" },
  { id: "nav.dropconfigs", labelKey: "DROP_CONFIGS", icon: "fa-box-archive", href: "/dropconfigs" },
  { id: "nav.farm", labelKey: "FARM_NAV", icon: "fa-wheat-awn", href: "/farm", flag: "FARM" },
  { id: "nav.demolish", labelKey: "DEMOLISH_NAV", icon: "fa-explosion", href: "/bot/demolish", flag: "DEMOLISH" },
  { id: "nav.orbs-trader", labelKey: "ORBS_TRADER_NAV", icon: "fa-cart-shopping", href: "/bot/orbs-trader", flag: "ORBS_TRADER" },
  { id: "nav.pokeball", labelKey: "POKEBALL_NAV", icon: "fa-circle-dot", href: "/bot/pokeball", flag: "POKEBALL" },
  { id: "nav.repair", labelKey: "REPAIR_NAV", icon: "fa-hammer", href: "/bot/repair", flag: "REPAIR" },
  { id: "nav.cropfarmer", labelKey: "CROP_FARMER_NAV", icon: "fa-wheat-awn", href: "/bot/crop-farmer", flag: "CROP_FARMER" },
  { id: "nav.villagertrading", labelKey: "VILLAGER_TRADING_NAV", icon: "fa-store", href: "/bot/villager-trading", flag: "VILLAGER_TRADING" },
  { id: "nav.caseopening", labelKey: "CASE_OPENING_NAV", icon: "fa-box-open", href: "/bot/case-opening", flag: "CASE_OPENING" },
  { id: "nav.inventory", labelKey: "INVENTORY", icon: "fa-cube", href: "/inventory" },
  { id: "nav.storages", labelKey: "STORAGES", icon: "fa-warehouse", href: "/inventory/storages" },
  { id: "nav.invaudit", labelKey: "INVENTORY_AUDIT_LOG", icon: "fa-clipboard-list", href: "/inventory/audit" },
  { id: "nav.audit", labelKey: "AUDITLOGS", icon: "fa-bolt", href: "/auditlogs" },
  { id: "nav.activity", labelKey: "ACTIVITY_FEED_NAV", icon: "fa-stream", href: "/activity" },
  { id: "nav.events", labelKey: "EVENTVIEWER", icon: "fa-bolt-lightning", href: "/eventviewer" },
  { id: "nav.players", labelKey: "ONLINE_PLAYERS_NAV", icon: "fa-users", href: "/online-players" },
  { id: "nav.biomes", labelKey: "BIOME_EXPLORER_NAV", icon: "fa-tree", href: "/biomes" },
  { id: "nav.jobs", labelKey: "JOBS_NAV", icon: "fa-briefcase", href: "/jobs" },
  { id: "nav.plugins", labelKey: "BOT_PLUGINS", icon: "fa-puzzle-piece", href: "/botplugins" },
  { id: "nav.tenants", labelKey: "ADMIN_TENANT_MANAGEMENT", icon: "fa-building", href: "/admin/tenants" },
  { id: "nav.proxies", labelKey: "PROXY_NAV", icon: "fa-network-wired", href: "/admin/proxies" },
  { id: "nav.oauth", labelKey: "MYOAUTHCLIENTS", icon: "fa-globe", href: "/oauthclients" },
  { id: "nav.tenantsettings", labelKey: "TENANT_SETTINGS", icon: "fa-sliders", href: "/tenant/settings" },
  { id: "nav.tenantmembers", labelKey: "TENANT_MEMBERS", icon: "fa-users", href: "/tenant/members" },
  { id: "nav.settings", labelKey: "SETTINGS", icon: "fa-gear", href: "/settings" },
  { id: "nav.account", labelKey: "ACCOUNT_NAV", icon: "fa-user", href: "/me" },
  { id: "nav.goggles", labelKey: "GOGGLES_NAV", icon: "fa-vr-cardboard", href: "/goggles" },
  { id: "nav.vektorutils", labelKey: "VEKTORUTILS_NAV", icon: "fa-toolbox", href: "/vektorutils" },
];

function fuzzyScore(query: string, target: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  if (t === q) return 1000;
  if (t.startsWith(q)) return 500;
  if (t.includes(q)) return 200;
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length ? 50 : 0;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal("");
  const [activeIdx, setActiveIdx] = createSignal(0);
  let inputRef: HTMLInputElement | undefined;

  const isFlagEnabled = (flag?: string) => !flag || isFeatureEnabled(flag);

  const filtered = createMemo(() => {
    const q = query();
    return ROUTE_ACTIONS
      .filter((a) => isFlagEnabled(a.flag))
      .map((a) => ({ a, score: fuzzyScore(q, t(a.labelKey)) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.a)
      .slice(0, 12);
  });

  function close() {
    setOpen(false);
    setQuery("");
    setActiveIdx(0);
  }

  function run(action: PaletteAction) {
    if (action.href) navigate(action.href);
    if (action.run) action.run();
    close();
  }

  function onKey(e: KeyboardEvent) {
    const k = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && k === "k") {
      e.preventDefault();
      setOpen((v) => !v);
      if (open()) {
        setTimeout(() => inputRef?.focus(), 0);
      }
      return;
    }
    if (!open()) return;
    if (k === "escape") {
      e.preventDefault();
      close();
      return;
    }
    if (k === "arrowdown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered().length - 1, i + 1));
      return;
    }
    if (k === "arrowup") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (k === "enter") {
      e.preventDefault();
      const sel = filtered()[activeIdx()];
      if (sel) run(sel);
      return;
    }
  }

  onMount(() => document.addEventListener("keydown", onKey));
  onCleanup(() => document.removeEventListener("keydown", onKey));

  return (
    <Show when={open()}>
      <div class={styles.overlay} onClick={close}>
        <div class={styles.panel} onClick={(e) => e.stopPropagation()}>
          <div class={styles.searchRow}>
            <i class="fa-solid fa-magnifying-glass" />
            <input
              ref={inputRef}
              class={styles.input}
              type="text"
              placeholder={t('CMDP_PLACEHOLDER')}
              value={query()}
              onInput={(e) => { setQuery(e.currentTarget.value); setActiveIdx(0); }}
              autofocus
            />
            <kbd class={styles.kbd}>Esc</kbd>
          </div>
          <ul class={styles.list}>
            <For each={filtered()}>
              {(a, i) => (
                <li
                  class={`${styles.item} ${i() === activeIdx() ? styles.active : ""}`}
                  onClick={() => run(a)}
                  onMouseEnter={() => setActiveIdx(i())}
                >
                  <Show when={a.icon}><i class={`fa-solid ${a.icon}`} /></Show>
                  <span class={styles.label}>{t(a.labelKey)}</span>
                  <Show when={a.hint}><span class={styles.hint}>{a.hint}</span></Show>
                </li>
              )}
            </For>
            <Show when={filtered().length === 0}>
              <li class={styles.noResults}>{t('CMDP_NO_MATCHES')}</li>
            </Show>
          </ul>
          <div class={styles.foot}>
            <span><kbd class={styles.kbd}>↑</kbd><kbd class={styles.kbd}>↓</kbd> {t('CMDP_NAVIGATE')}</span>
            <span><kbd class={styles.kbd}>Enter</kbd> {t('CMDP_OPEN')}</span>
            <span><kbd class={styles.kbd}>Ctrl</kbd>+<kbd class={styles.kbd}>K</kbd> {t('CMDP_TOGGLE')}</span>
          </div>
        </div>
      </div>
    </Show>
  );
}
